import { setIsRefreshingMembers, setSelfLeaveChannel } from "../stores";
import { AddMembersProps, ChatChannel, ChatGroupBody, ChatPhoto, MakeModeratorProps, RemoveMemberProps } from "../types";
import { BaseChatPackage } from "../utils";
import { alert } from '@codeleap/modals'

type UpdateGroupBody = Pick<ChatGroupBody, 'name' | 'image'> & { image: File }
export class GroupsServices extends BaseChatPackage {
  async createGroup({ members, name, image }: ChatGroupBody & { image: File }) {
   try {
     let payload = {
      members: members?.map(user => ({ id: user.id, name: user.name })),
      ...(name && { name }),
      image: null
    }

    if (image) {
      const response = await this.chatApi.createPhoto(image)

      if (response) {
        const photo = response?.data as ChatPhoto
        payload.image = photo.file
      }
    }

    const response = await this.chatApi.createGroup(payload)
    const channelId = response?.data?.id

    if (channelId) {
      await this.chatApi.sendMessageSystem({ channel: { id: channelId, type: 'team' }, text: `created the group` })
    }
  
    return response
   }catch(e) {
      console.error('Error creating group:', e)
      alert.error({
        title: 'Error',
        body: `Error while trying to create the group`,
      })
   }
  }

  async updateGroup(id: ChatChannel['id'], body: UpdateGroupBody, initialBody: Pick<UpdateGroupBody, 'name'>) {
    try {
      const { image, name } = body

      let payload = {
        name,
        image: null
      }


      if (image) {
        const response = await this.chatApi.createPhoto(image)

        if (response) {
          const photo = response?.data as ChatPhoto
          payload.image = photo?.file 
        }
      }

      const response = await this.chatApi.updateGroup(id, payload)

      const hasNameChanged = !!name && name !== initialBody.name
      const hasImageChanged = !!payload.image

      // Fire-and-forget: the group is already updated, so a failed system
      // message must not surface an "update failed" alert or escape as an
      // unhandled rejection. Same pattern as leaveGroup below.
      if (hasImageChanged) {
        this.chatApi.sendMessageSystem({ channel: { id, type: 'team' }, text: `changed this group's icon` }).catch(error => {
          console.warn('Failed to send group icon system message:', error)
        })
      }

      if (hasNameChanged) {
        this.chatApi.sendMessageSystem({ channel: { id, type: 'team' }, text: `changed the group name to "${name}"` }).catch(error => {
          console.warn('Failed to send group name system message:', error)
        })
      }
      return response
    }catch(e) {
      console.error('Error updating group:', e)
      alert.error({
        title: 'Error',
        body: `Error while trying to update the group`,
      })
    } 
  }

  async leaveGroup({ channelId, member, onSuccess, onError, onFinally }: RemoveMemberProps) {
    try {
      setSelfLeaveChannel(channelId)
      // send_system_message=false: leaving emits its own `left` message below.
      // Letting the backend also emit `removed` produced a duplicate
      // "UserX removed UserX" alongside "UserX left" (Notion 3611e3f8...2465d / ...27b236).
      await this.chatApi.removeMember(channelId, member?.id, false)
      this.chatApi.sendMessageSystem({ channel: { id: channelId, type: 'team' }, text: `left` }).catch(error => {
        console.warn('Failed to send leave system message:', error)
      })
      onSuccess?.(null)
    } catch (e) {
      onError?.(e as Error)
      console.error('Error while remove members:', e)
      alert.error({
        title: 'Error',
        body: `Error while trying to remove member to the group`,
      })
    } finally {
      onFinally?.()
    }
  }

  async addMembers({ channelId, selectedMembers, onSuccess, onError, onFinally }: AddMembersProps) {
    try {
      setIsRefreshingMembers(true)
      const members = selectedMembers.map(user => ({ id: user.id, name: user.name }))
      await this.chatApi.addMembers(channelId, members)
      onSuccess?.(null)
    } catch (e) {
      onError?.(e as Error)
      console.error('Error while adding members:', e)
      alert.error({
        title: 'Error',
        body: `Error while trying to add member to the group`,
      })
    } finally {
      onFinally?.()
    }
  }

  async removeMember({ channelId, member, onSuccess, onError, onFinally }: RemoveMemberProps) {
    try {
      const response = await this.chatApi.removeMember(channelId, member?.id)
      onSuccess?.(null)
      return response
    } catch (e) {
      onError?.(e as Error)
      console.error('Error while remove members:', e)
      alert.error({
        title: 'Error',
        body: `Error while trying to remove member to the group`,
      })
    } finally {
      onFinally?.()
    }
  }

  async addModerator({ channelId, member }: MakeModeratorProps) {
    try {
      const memberId = member?.user_id as string
      const memberName = (member as any)?.user?.name || 'Someone'
      const response = await this.chatApi.addModerators(channelId, [{ id: memberId, name: memberName }])
      this.queries?.members.manager.updateItem(memberId, { ...member, role: 'moderator', is_moderator: true, 'channel_role': 'channel_moderator'})
      return response
    } catch (e) {
      alert.error({
        title: 'Error',
        body: `Error while trying to make a group member an admin`,
      })
    }
  }

  async removeModerator({ channelId, member }: MakeModeratorProps) {
    try {
      const memberId = member?.user_id as string
      const response = await this.chatApi.removeModerator(channelId, memberId)
      this.queries?.members.manager.updateItem(memberId, { ...member, role: 'member', is_moderator: false, channel_role: 'channel_member'})
      return response
    } catch (e) {
      alert.error({
        title: 'Error',
        body: `Error while trying to remove a group member as a moderator`,
      })
    }
  }
}
